import React, { useEffect, useMemo, useRef, useState } from "react";

const SIZE = 10;

const CLUE_LAYOUT = [
  {
    number: 1,
    direction: "Across",
    length: 3,
    row: 5,
    col: 1,
    operation: "LCM",
  },
  {
    number: 2,
    direction: "Down",
    length: 3,
    row: 5,
    col: 1,
    operation: "HCF",
  },
  {
    number: 3,
    direction: "Down",
    length: 3,
    row: 3,
    col: 3,
    operation: "LCM",
  },
  {
    number: 4,
    direction: "Across",
    length: 3,
    row: 7,
    col: 1,
    operation: "LCM",
  },
  {
    number: 5,
    direction: "Across",
    length: 3,
    row: 3,
    col: 3,
    operation: "LCM",
  },
  {
    number: 6,
    direction: "Down",
    length: 3,
    row: 7,
    col: 3,
    operation: "LCM",
  },
  {
    number: 7,
    direction: "Down",
    length: 3,
    row: 1,
    col: 5,
    operation: "LCM",
  },
  {
    number: 8,
    direction: "Across",
    length: 2,
    row: 9,
    col: 2,
    operation: "HCF",
  },
  {
    number: 9,
    direction: "Across",
    length: 3,
    row: 1,
    col: 5,
    operation: "HCF",
  },
  {
    number: 10,
    direction: "Down",
    length: 2,
    row: 0,
    col: 7,
    operation: "LCM",
  },
];

const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

const lcm = (a, b) => (a * b) / gcd(a, b);

const choose = (items) =>
  items[Math.floor(Math.random() * items.length)];

const primeFactorisation = (number) => {
  const primeCounts = {};
  let remainder = number;

  for (let divisor = 2; divisor <= remainder; divisor += 1) {
    while (remainder % divisor === 0) {
      primeCounts[divisor] = (primeCounts[divisor] || 0) + 1;
      remainder /= divisor;
    }
  }

  const superscripts = {
    2: "²",
    3: "³",
    4: "⁴",
    5: "⁵",
    6: "⁶",
  };

  return Object.entries(primeCounts)
    .map(
      ([prime, power]) =>
        `${prime}${
          power > 1 ? superscripts[power] || `^${power}` : ""
        }`
    )
    .join(" × ");
};

const makeHcfPair = (target) => {
  const coprimeMultipliers = [
    [4, 9],
    [5, 8],
    [7, 9],
    [8, 9],
    [8, 11],
    [9, 10],
    [11, 12],
  ];

  const [firstMultiplier, secondMultiplier] =
    choose(coprimeMultipliers);

  return [
    target * firstMultiplier,
    target * secondMultiplier,
  ];
};

const isComposite = (number) => {
  if (!Number.isInteger(number) || number < 4) return false;
  for (let divisor = 2; divisor <= Math.sqrt(number); divisor += 1) {
    if (number % divisor === 0) return true;
  }
  return false;
};

const primeFactorCount = (number) => {
  let remainder = number;
  let count = 0;
  for (let divisor = 2; divisor <= remainder; divisor += 1) {
    while (remainder % divisor === 0) {
      count += 1;
      remainder /= divisor;
    }
  }
  return count;
};

const getLcmPairs = (target) => {
  const safeTarget = Number(target);

  if (
    !Number.isSafeInteger(safeTarget) ||
    safeTarget < 2 ||
    safeTarget > 9999
  ) {
    return [];
  }

  const divisors = [];

  for (
    let value = 2;
    value < safeTarget;
    value += 1
  ) {
    if (safeTarget % value === 0) {
      divisors.push(value);
    }
  }

  const pairs = [];

  for (
    let firstIndex = 0;
    firstIndex < divisors.length;
    firstIndex += 1
  ) {
    for (
      let secondIndex = firstIndex + 1;
      secondIndex < divisors.length;
      secondIndex += 1
    ) {
      const first = divisors[firstIndex];
      const second = divisors[secondIndex];

      const isWorthFactorising =
        isComposite(first) &&
        isComposite(second) &&
        Math.min(first, second) >= 6 &&
        primeFactorCount(first) + primeFactorCount(second) >= 5;

      if (lcm(first, second) === safeTarget && isWorthFactorising) {
        pairs.push([first, second]);
      }
    }
  }

  return pairs;
};

const makeLcmPair = (target) => choose(getLcmPairs(target));

const layoutCells = (layout) =>
  Array.from({ length: layout.length }, (_, index) => ({
    row:
      layout.row +
      (layout.direction === "Down" ? index : 0),
    col:
      layout.col +
      (layout.direction === "Across" ? index : 0),
  }));

const ANSWER_SETS = [
  ["390", "333", "270", "352", "248", "280", "588", "30", "574", "54"],
  ["228", "234", "328", "492", "360", "240", "200", "10", "256", "56"],
  ["588", "541", "288", "104", "240", "448", "400", "18", "490", "40"],
  ["376", "361", "216", "144", "264", "416", "384", "36", "310", "40"],
  ["372", "375", "342", "544", "324", "495", "444", "45", "486", "36"],
  ["440", "442", "260", "264", "204", "432", "444", "52", "414", "24"],
  ["140", "185", "270", "532", "232", "210", "392", "30", "368", "48"],
  ["350", "385", "500", "592", "558", "280", "368", "40", "396", "36"],
  ["368", "354", "408", "495", "414", "552", "564", "42", "540", "40"],
  ["536", "572", "496", "272", "414", "252", "294", "22", "218", "48"],
  ["288", "293", "558", "372", "550", "248", "580", "48", "554", "24"],
  ["432", "494", "112", "424", "132", "464", "152", "54", "168", "48"],
  ["378", "362", "208", "252", "225", "276", "405", "46", "484", "54"],
  ["260", "231", "500", "135", "550", "585", "480", "35", "416", "36"],
  ["488", "443", "468", "375", "440", "592", "220", "32", "270", "40"],
  ["510", "503", "330", "372", "336", "276", "126", "36", "108", "48"],
  ["480", "434", "160", "405", "168", "520", "208", "10", "268", "48"],
  ["264", "204", "364", "495", "396", "592", "336", "22", "314", "24"],
  ["272", "252", "552", "234", "592", "476", "152", "16", "106", "36"],
  ["328", "381", "108", "104", "176", "400", "276", "30", "208", "48"],
  ["336", "392", "296", "252", "288", "224", "208", "14", "276", "36"],
  ["260", "214", "160", "405", "184", "576", "414", "16", "426", "36"],
  ["520", "501", "340", "104", "360", "424", "460", "14", "404", "54"],
  ["330", "321", "320", "162", "360", "234", "400", "34", "456", "36"],
  ["560", "564", "150", "432", "180", "225", "300", "35", "334", "24"],
  ["250", "232", "550", "204", "585", "408", "405", "58", "426", "36"],
  ["594", "573", "484", "352", "456", "220", "136", "20", "184", "24"],
  ["250", "285", "460", "513", "480", "342", "540", "52", "548", "48"],
  ["476", "453", "546", "342", "536", "204", "136", "14", "196", "36"],
  ["140", "102", "160", "204", "192", "464", "572", "14", "564", "24"],
];

const generateAnswerLayout = () => {
  const answers = choose(ANSWER_SETS);

  return CLUE_LAYOUT.map((layout, index) => ({
    ...layout,
    answer: answers[index],
  }));
};

const generateClues = (answerLayout) =>
  answerLayout.map((layout) => {
    const target = Number(layout.answer);

    const [first, second] =
      layout.operation === "HCF"
        ? makeHcfPair(target)
        : makeLcmPair(target);

    const clue = `Use prime factorisation to find the ${
      layout.operation
    } of ${first.toLocaleString()} and ${second.toLocaleString()}.`;

    return {
      ...layout,
      clue,
      operands: [first, second],
    };
  });

const buildClues = () => {
  let answerLayout = generateAnswerLayout();
  let generated = generateClues(answerLayout);

  if (typeof window === "undefined") {
    return generated;
  }

  try {
    const storageKey =
      "hcf-lcm-crossword-last-answers";

    const previousSignature =
      window.sessionStorage.getItem(storageKey);

    let signature = JSON.stringify(
      generated.map(({ answer }) => answer)
    );

    /*
     * If the randomly generated answers happen to match the
     * previous crossword, generate another set.
     */
    for (
      let attempt = 0;
      attempt < 10 && signature === previousSignature;
      attempt += 1
    ) {
      answerLayout = generateAnswerLayout();
      generated = generateClues(answerLayout);

      signature = JSON.stringify(
        generated.map(({ answer }) => answer)
      );
    }

    window.sessionStorage.setItem(storageKey, signature);
  } catch {
    /*
     * Random questions will still work if browser storage
     * is unavailable.
     */
  }

  return generated;
};

const cellsFor = (clue) =>
  [...clue.answer].map((_, index) => ({
    row:
      clue.row +
      (clue.direction === "Down" ? index : 0),
    col:
      clue.col +
      (clue.direction === "Across" ? index : 0),
  }));

const cellKey = (row, col) => `${row}-${col}`;

const BEST_TIME_KEY =
  "hcf-lcm-crossword-best-time";

const formatTime = (milliseconds) => {
  const totalTenths = Math.floor(milliseconds / 100);
  const minutes = Math.floor(totalTenths / 600);
  const seconds = Math.floor(
    (totalTenths % 600) / 10
  );
  const tenths = totalTenths % 10;

  return `${String(minutes).padStart(2, "0")}:${String(
    seconds
  ).padStart(2, "0")}.${tenths}`;
};

export default function HcfLcmNumberCrossword() {
  const [clues] = useState(buildClues);
  const [values, setValues] = useState({});
  const [activeClue, setActiveClue] = useState(1);
  const [results, setResults] = useState({});
  const [score, setScore] = useState(0);
  const [hints, setHints] = useState(3);

  const [message, setMessage] = useState(
    "Choose a clue and enter one digit in each square."
  );

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const [bestTime, setBestTime] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }

    try {
      const saved = Number(
        window.localStorage.getItem(BEST_TIME_KEY)
      );

      return saved > 0 ? saved : null;
    } catch {
      return null;
    }
  });

  const inputRefs = useRef({});

  useEffect(() => {
    if (!started || startedAt === null) {
      return undefined;
    }

    const updateStopwatch = () => {
      setElapsedTime(Date.now() - startedAt);
    };

    updateStopwatch();

    const timer = window.setInterval(
      updateStopwatch,
      100
    );

    return () => window.clearInterval(timer);
  }, [started, startedAt]);

  const grid = useMemo(() => {
    const map = {};

    clues.forEach((clue) => {
      cellsFor(clue).forEach(
        ({ row, col }, index) => {
          const key = cellKey(row, col);

          if (!map[key]) {
            map[key] = {
              row,
              col,
              clues: [],
              answerDigit: clue.answer[index],
            };
          }

          map[key].clues.push(clue.number);
        }
      );
    });

    return map;
  }, [clues]);

  const selected = clues.find(
    (clue) => clue.number === activeClue
  );

  const completed = clues.filter(
    (clue) => results[clue.number] === "correct"
  ).length;

  const selectClue = (number) => {
    setActiveClue(number);

    const clue = clues.find(
      (item) => item.number === number
    );

    const positions = cellsFor(clue);

    const firstEmpty =
      positions.find(
        ({ row, col }) =>
          !values[cellKey(row, col)]
      ) || positions[0];

    window.setTimeout(() => {
      inputRefs.current[
        cellKey(firstEmpty.row, firstEmpty.col)
      ]?.focus();
    }, 0);
  };

  const startCrossword = () => {
    const now = Date.now();

    setElapsedTime(0);
    setStartedAt(now);
    setStarted(true);
    setFinished(false);

    setMessage(
      "Select a clue and enter one digit in each square."
    );

    window.setTimeout(() => selectClue(1), 0);
  };

  const finishCrossword = () => {
    const finalTime =
      startedAt === null
        ? elapsedTime
        : Date.now() - startedAt;

    const isNewBest =
      bestTime === null || finalTime < bestTime;

    setElapsedTime(finalTime);
    setStarted(false);
    setFinished(true);

    setBestTime((currentBest) => {
      const newBest =
        currentBest === null ||
        finalTime < currentBest
          ? finalTime
          : currentBest;

      if (newBest !== currentBest) {
        try {
          window.localStorage.setItem(
            BEST_TIME_KEY,
            String(newBest)
          );
        } catch {
          /*
           * The result will still be displayed if browser
           * storage is unavailable.
           */
        }
      }

      return newBest;
    });

    return {
      finalTime,
      isNewBest,
    };
  };

  const updateDigit = (row, col, input) => {
    const digit = input
      .replace(/\D/g, "")
      .slice(-1);

    const key = cellKey(row, col);

    setValues((current) => ({
      ...current,
      [key]: digit,
    }));

    setResults((current) => {
      const next = { ...current };

      grid[key].clues.forEach((number) => {
        delete next[number];
      });

      return next;
    });

    if (digit) {
      const positions = cellsFor(selected);

      const index = positions.findIndex(
        (cell) =>
          cell.row === row && cell.col === col
      );

      const next = positions[index + 1];

      if (next) {
        inputRefs.current[
          cellKey(next.row, next.col)
        ]?.focus();
      }
    }
  };

  const handleKeyDown = (
    event,
    row,
    col
  ) => {
    const positions = cellsFor(selected);

    const index = positions.findIndex(
      (cell) =>
        cell.row === row && cell.col === col
    );

    if (
      event.key === "Backspace" &&
      !values[cellKey(row, col)] &&
      positions[index - 1]
    ) {
      event.preventDefault();

      inputRefs.current[
        cellKey(
          positions[index - 1].row,
          positions[index - 1].col
        )
      ]?.focus();
    }

    const change = {
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
    }[event.key];

    if (change) {
      event.preventDefault();

      inputRefs.current[
        cellKey(
          row + change[0],
          col + change[1]
        )
      ]?.focus();
    }
  };

  const checkClue = () => {
    const attempt = cellsFor(selected)
      .map(
        ({ row, col }) =>
          values[cellKey(row, col)] || ""
      )
      .join("");

    if (attempt.length !== selected.answer.length) {
      setMessage(
        "Fill every square for this clue before checking."
      );

      return;
    }

    if (attempt === selected.answer) {
      if (
        results[selected.number] !== "correct"
      ) {
        setScore((current) => current + 100);
      }

      setResults((current) => ({
        ...current,
        [selected.number]: "correct",
      }));

      const hasCompletedCrossword =
        clues.every(
          (clue) =>
            clue.number === selected.number ||
            results[clue.number] === "correct"
        );

      if (hasCompletedCrossword) {
        const { finalTime, isNewBest } =
          finishCrossword();

        setMessage(
          `Crossword complete in ${formatTime(
            finalTime
          )}.${
            isNewBest
              ? " This is a new best time."
              : " Excellent work."
          }`
        );
      } else {
        setMessage(
          `Correct. ${selected.direction} ${selected.number} = ${selected.answer}.`
        );

        const next = clues.find(
          (clue) =>
            results[clue.number] !== "correct" &&
            clue.number !== selected.number
        );

        if (next) {
          window.setTimeout(
            () => selectClue(next.number),
            450
          );
        }
      }
    } else {
      setResults((current) => ({
        ...current,
        [selected.number]: "wrong",
      }));

      setScore((current) =>
        Math.max(0, current - 20)
      );

      setMessage(
        "Not quite. Check the prime factors and try again!"
      );
    }
  };

  const useHint = () => {
    if (!hints) {
      setMessage(
        "You have used all three hints."
      );

      return;
    }

    const positions = cellsFor(selected);

    const emptyIndex = positions.findIndex(
      ({ row, col }, index) =>
        values[cellKey(row, col)] !==
        selected.answer[index]
    );

    if (emptyIndex < 0) {
      setMessage(
        "This answer is already filled in — try checking it!"
      );

      return;
    }

    const position = positions[emptyIndex];

    setValues((current) => ({
      ...current,
      [cellKey(position.row, position.col)]:
        selected.answer[emptyIndex],
    }));

    setHints((current) => current - 1);

    setMessage(
      `Hint used: one digit of ${selected.direction} ${selected.number} has been revealed.`
    );
  };

  const checkAll = () => {
    const nextResults = {};

    clues.forEach((clue) => {
      const attempt = cellsFor(clue)
        .map(
          ({ row, col }) =>
            values[cellKey(row, col)] || ""
        )
        .join("");

      nextResults[clue.number] =
        attempt === clue.answer
          ? "correct"
          : "wrong";
    });

    setResults(nextResults);

    const totalCorrect = Object.values(
      nextResults
    ).filter(
      (value) => value === "correct"
    ).length;

    if (totalCorrect === clues.length) {
      const { finalTime, isNewBest } =
        finishCrossword();

      setMessage(
        `Crossword complete in ${formatTime(
          finalTime
        )}.${
          isNewBest
            ? " This is a new best time."
            : " Excellent work."
        }`
      );
    } else {
      setMessage(
        `${totalCorrect} out of ${clues.length} clues are correct. Keep going.`
      );
    }
  };

  const reset = (
    requireConfirmation = false
  ) => {
    if (
      requireConfirmation &&
      !window.confirm(
        "Are you sure you want to reset the crossword? Your current answers, score and time will be lost."
      )
    ) {
      return;
    }

    setValues({});
    setResults({});
    setScore(0);
    setHints(3);
    setActiveClue(1);
    setStarted(false);
    setFinished(false);
    setStartedAt(null);
    setElapsedTime(0);

    setMessage(
      "Choose a clue and enter one digit in each square."
    );
  };

  return (
    <main className="crossword-shell">
      <style>{`
        * {
          box-sizing: border-box;
        }

        button,
        input {
          font-family: inherit;
        }

        .crossword-shell {
          min-height: 100vh;
          padding: 32px;
          color: #172033;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
          background: #eef2f6;
        }

        .game {
          width: min(1180px, 100%);
          margin: auto;
          overflow: hidden;
          border: 1px solid #d8dee8;
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 16px 40px rgba(15, 23, 42, 0.09);
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 22px;
          padding: 24px 30px;
          color: white;
          background: #19324d;
        }

        .title-wrap {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .title-icon {
          display: grid;
          flex: 0 0 auto;
          place-items: center;
          width: 62px;
          height: 48px;
          border: 1px solid #55718d;
          border-radius: 8px;
          color: #dceaf4;
          background: #25445f;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.04em;
        }

        .eyebrow {
          margin: 0 0 4px;
          color: #7dd3c7;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.13em;
          text-transform: uppercase;
        }

        h1 {
          margin: 0;
          color: white;
          font-size: clamp(1.45rem, 3.5vw, 2.2rem);
          line-height: 1.1;
          letter-spacing: -0.02em;
          font-weight: 700;
        }

        .subtitle {
          margin: 6px 0 0;
          color: #c9d6e3;
          font-size: 0.9rem;
        }

        .stats {
          display: flex;
          gap: 8px;
        }

        .pill {
          display: flex;
          align-items: center;
          min-height: 38px;
          padding: 8px 12px;
          border: 1px solid #49647e;
          border-radius: 7px;
          color: #dce7f0;
          background: #223d57;
          font-size: 0.86rem;
          font-weight: 600;
          white-space: nowrap;
        }

        .timer-pill {
          min-width: 96px;
          justify-content: center;
          font-variant-numeric: tabular-nums;
          letter-spacing: 0.035em;
        }

        .progress {
          height: 5px;
          background: #e3e8ee;
        }

        .progress div {
          height: 100%;
          background: #2d8c82;
          transition: width 0.35s ease;
        }

        .start-screen {
          display: grid;
          min-height: 560px;
          place-items: center;
          padding: 40px 24px;
          background: #f7f9fb;
        }

        .start-card {
          width: min(100%, 520px);
          padding: 42px;
          border: 1px solid #d7dfe7;
          border-radius: 12px;
          background: white;
          text-align: center;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
        }

        .start-mark {
          display: grid;
          width: 64px;
          height: 64px;
          margin: 0 auto 22px;
          place-items: center;
          border-radius: 50%;
          color: white;
          background: #287f78;
          font-size: 1.15rem;
          font-weight: 700;
        }

        .start-card h2 {
          margin: 0 0 10px;
          color: #19324d;
          font-size: 1.7rem;
        }

        .start-card p {
          max-width: 420px;
          margin: 0 auto 24px;
          color: #5b6b7b;
          line-height: 1.6;
        }

        .start-button {
          min-width: 190px;
          min-height: 48px;
          border: 1px solid #19324d;
          border-radius: 7px;
          color: white;
          background: #19324d;
          font-size: 1rem;
          font-weight: 650;
          cursor: pointer;
        }

        .start-button:hover {
          background: #254864;
        }

        .content {
          display: grid;
          grid-template-columns:
            minmax(410px, 0.92fr)
            1.08fr;
          gap: 30px;
          padding: 30px;
        }

        .board-panel {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          border: 1px solid #dce2ea;
          border-radius: 12px;
          background: #f8fafc;
        }

        .board-label {
          align-self: stretch;
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 0 2px 13px;
          color: #475569;
          font-size: 0.84rem;
          font-weight: 850;
        }

        .board-label span:first-child {
          color: #19324d;
          font-size: 1rem;
          font-weight: 700;
        }

        .board {
          display: grid;
          width: min(100%, 540px);
          aspect-ratio: 1;
          grid-template-columns: repeat(${SIZE}, 1fr);
          grid-template-rows: repeat(${SIZE}, 1fr);
          gap: 2px;
          padding: 8px;
          border: 1px solid #26394d;
          border-radius: 8px;
          background: #26394d;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
        }

        .block {
          background: #26394d;
        }

        .square {
          position: relative;
          overflow: hidden;
          border: 1px solid #aeb9c6;
          border-radius: 2px;
          background: #ffffff;
          transition:
            border-color 0.14s,
            box-shadow 0.14s,
            background 0.14s;
        }

        .square:focus-within {
          z-index: 3;
          border-color: #287f78;
          box-shadow: inset 0 0 0 2px #287f78;
        }

        .square.active {
          border-color: #74aaa5;
          background: #edf7f6;
        }

        .square.correct {
          border-color: #b48b2f;
          background: #f7edcc;
          box-shadow: inset 0 0 0 1px #d8bd72;
        }

        .square.wrong {
          border-color: #b56565;
          background: #fbefef;
        }

        .number {
          position: absolute;
          z-index: 2;
          top: 2px;
          left: 4px;
          color: #475569;
          font-size: clamp(0.48rem, 1.05vw, 0.7rem);
          font-weight: 950;
          line-height: 1;
          pointer-events: none;
        }

        .square input {
          width: 100%;
          height: 100%;
          padding: 8px 0 0;
          border: 0;
          outline: 0;
          color: #172033;
          background: transparent;
          text-align: center;
          font-size: clamp(1.25rem, 3.2vw, 2rem);
          font-weight: 700;
          caret-color: #287f78;
        }

        .message {
          width: min(100%, 540px);
          min-height: 58px;
          margin-top: 22px;
          padding: 13px 17px;
          border: 1px solid #ccd6e0;
          border-radius: 8px;
          text-align: left;
          color: #31465a;
          background: white;
          font-weight: 600;
        }

        .clue-panel {
          min-width: 0;
        }

        .clue-heading {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 4px;
        }

        .clue-heading-icon {
          display: grid;
          place-items: center;
          width: 38px;
          height: 38px;
          border-radius: 7px;
          color: white;
          background: #287f78;
          font-size: 0.76rem;
          font-weight: 700;
        }

        .clue-panel h2 {
          margin: 0;
          font-size: 1.55rem;
          letter-spacing: -0.02em;
        }

        .instructions {
          margin: 7px 0 17px;
          color: #64748b;
          line-height: 1.5;
        }

        .clues {
          display: grid;
          grid-template-columns: 1fr 1fr;
          align-items: start;
          gap: 14px;
        }

        .clue-group {
          padding: 14px;
          border: 1px solid #dce2e9;
          border-radius: 10px;
          background: #f8fafc;
        }

        .clue-group h3 {
          margin: 0 0 10px;
          padding: 0 2px 9px;
          border-bottom: 1px solid #dce2e9;
          color: #19324d;
          font-size: 0.9rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .clue {
          display: flex;
          align-items: flex-start;
          width: 100%;
          gap: 9px;
          margin: 6px 0;
          padding: 10px;
          border: 1px solid transparent;
          border-radius: 7px;
          color: #334155;
          background: transparent;
          text-align: left;
          font: inherit;
          line-height: 1.4;
          cursor: pointer;
          transition:
            border-color 0.14s,
            background 0.14s;
        }

        .clue:hover {
          border-color: #c5d1dc;
          background: white;
        }

        .clue.selected {
          border-color: #8ebcb8;
          background: #edf7f6;
        }

        .clue.correct {
          border-color: #d0b15c;
          background: #f8efd5;
        }

        .clue.wrong {
          border-color: #d7adad;
          background: #fbf2f2;
        }

        .clue strong {
          display: grid;
          flex: 0 0 26px;
          place-items: center;
          width: 26px;
          height: 26px;
          border-radius: 5px;
          color: white;
          background: #536b80;
          font-size: 0.75rem;
        }

        .clue.selected strong {
          background: #287f78;
        }

        .clue.correct strong {
          color: #2f2818;
          background: #d5b45d;
        }

        .clue.wrong strong {
          background: #a55757;
        }

        .status-text {
          display: block;
          margin-top: 4px;
          color: #80651f;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .selected-clue {
          position: relative;
          margin: 17px 0;
          padding: 15px 17px 15px 20px;
          overflow: hidden;
          border: 1px solid #c9d5df;
          border-radius: 8px;
          color: #24384b;
          background: #f5f8fa;
          font-size: 1rem;
          line-height: 1.45;
        }

        .selected-clue::before {
          content: "";
          position: absolute;
          inset: 0 auto 0 0;
          width: 4px;
          background: #287f78;
        }

        .completion-card {
          margin: 17px 0;
          padding: 18px;
          border: 1px solid #d0b15c;
          border-radius: 8px;
          color: #433817;
          background: #f8efd5;
        }

        .completion-card h3 {
          margin: 0 0 10px;
          color: #433817;
          font-size: 1.05rem;
        }

        .time-results {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .time-result {
          padding: 11px 12px;
          border: 1px solid #ddc983;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.55);
        }

        .time-result span {
          display: block;
          margin-bottom: 3px;
          color: #745f24;
          font-size: 0.72rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .time-result strong {
          font-size: 1.25rem;
          font-variant-numeric: tabular-nums;
        }

        .actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .action {
          min-height: 44px;
          padding: 10px 14px;
          border: 1px solid #19324d;
          border-radius: 7px;
          color: white;
          background: #19324d;
          font: inherit;
          font-size: 0.9rem;
          font-weight: 650;
          cursor: pointer;
          transition:
            background 0.14s,
            border-color 0.14s;
        }

        .action:hover {
          background: #254864;
          border-color: #254864;
        }

        .action:active {
          background: #102a42;
        }

        .action.yellow {
          color: #20423f;
          border-color: #9fc8c4;
          background: #e8f4f2;
        }

        .action.yellow:hover {
          background: #d9ece9;
        }

        .action.blue {
          border-color: #287f78;
          background: #287f78;
        }

        .action.blue:hover {
          background: #216c66;
        }

        .action.grey {
          color: #3e5061;
          border-color: #c9d2dc;
          background: #f4f6f8;
        }

        .action.grey:hover {
          background: #e8edf1;
        }

        @media (max-width: 930px) {
          .content {
            grid-template-columns: 1fr;
          }

          .board-panel {
            max-width: 650px;
            width: 100%;
            margin: auto;
          }
        }

        @media (max-width: 650px) {
          .crossword-shell {
            padding: 9px;
          }

          .game {
            border-radius: 12px;
          }

          .header {
            align-items: flex-start;
            flex-direction: column;
            padding: 18px;
          }

          .content {
            padding: 14px;
            gap: 20px;
          }

          .board-panel {
            padding: 10px;
          }

          .clues {
            grid-template-columns: 1fr;
          }

          .stats {
            width: 100%;
            flex-wrap: wrap;
          }

          .pill {
            flex: 1;
            justify-content: center;
          }

          .start-screen {
            min-height: 470px;
            padding: 20px;
          }

          .start-card {
            padding: 30px 22px;
          }
        }

        @media (max-width: 420px) {
          .title-icon {
            width: 46px;
            height: 46px;
          }

          .subtitle {
            display: none;
          }

          .board {
            gap: 2px;
            padding: 6px;
            border-width: 3px;
          }

          .actions {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <section className="game">
        <header className="header">
          <div className="title-wrap">
            <span className="title-icon">
              HCF
              <br />
              LCM
            </span>

            <div>
              <p className="eyebrow">
                Prime Factor Challenge
              </p>

              <h1>
                HCF &amp; LCM Number Crossword
              </h1>

              <p className="subtitle">
                Solve the clues. Match the digits.
                Complete the grid.
              </p>
            </div>
          </div>

          <div className="stats">
            <span className="pill timer-pill">
              Time&nbsp; {formatTime(elapsedTime)}
            </span>

            <span className="pill">
              Score&nbsp; {score}
            </span>

            <span className="pill">
              Hints&nbsp; {hints}
            </span>

            <span className="pill">
              Solved&nbsp; {completed}/{clues.length}
            </span>

            {finished && bestTime !== null && (
              <span className="pill timer-pill">
                Best&nbsp; {formatTime(bestTime)}
              </span>
            )}
          </div>
        </header>

        <div className="progress">
          <div
            style={{
              width: `${
                (completed / clues.length) * 100
              }%`,
            }}
          />
        </div>

        {!started && !finished ? (
          <section className="start-screen">
            <div className="start-card">
              <span className="start-mark">
                00:00
              </span>

              <h2>Ready to begin?</h2>

              <p>
                The stopwatch starts when you select
                Start. Complete all ten HCF and LCM
                clues as accurately and quickly as
                you can.
              </p>

              <button
                className="start-button"
                onClick={startCrossword}
              >
                Start crossword
              </button>
            </div>
          </section>
        ) : (
          <div className="content">
            <section className="board-panel">
              <div className="board-label">
                <span>Crossword grid</span>
                <span>One digit per square</span>
              </div>

              <div className="board">
                {Array.from(
                  { length: SIZE * SIZE },
                  (_, index) => {
                    const row = Math.floor(
                      index / SIZE
                    );

                    const col = index % SIZE;
                    const key = cellKey(row, col);
                    const cell = grid[key];

                    if (!cell) {
                      return (
                        <div
                          className="block"
                          key={key}
                        />
                      );
                    }

                    const isActive =
                      cell.clues.includes(activeClue);

                    const statuses =
                      cell.clues.map(
                        (number) =>
                          results[number]
                      );

                    const status =
                      statuses.includes("correct")
                        ? "correct"
                        : statuses.includes("wrong")
                        ? "wrong"
                        : "";

                    const startingClues =
                      clues.filter(
                        (clue) =>
                          clue.row === row &&
                          clue.col === col
                      );

                    return (
                      <label
                        key={key}
                        className={`square ${
                          isActive ? "active" : ""
                        } ${status}`}
                      >
                        {startingClues.length >
                          0 && (
                          <span className="number">
                            {startingClues
                              .map(
                                (clue) =>
                                  clue.number
                              )
                              .join("/")}
                          </span>
                        )}

                        <input
                          ref={(element) => {
                            inputRefs.current[
                              key
                            ] = element;
                          }}
                          value={
                            values[key] || ""
                          }
                          inputMode="numeric"
                          maxLength={1}
                          disabled={finished}
                          aria-label={`Crossword square row ${
                            row + 1
                          }, column ${col + 1}`}
                          onFocus={() => {
                            if (!isActive) {
                              setActiveClue(
                                cell.clues[0]
                              );
                            }
                          }}
                          onChange={(event) =>
                            updateDigit(
                              row,
                              col,
                              event.target.value
                            )
                          }
                          onKeyDown={(event) =>
                            handleKeyDown(
                              event,
                              row,
                              col
                            )
                          }
                        />
                      </label>
                    );
                  }
                )}
              </div>

              <div
                className="message"
                aria-live="polite"
              >
                {message}
              </div>
            </section>

            <section className="clue-panel">
              <div className="clue-heading">
                <span className="clue-heading-icon">
                  01
                </span>

                <h2>Clues</h2>
              </div>

              <p className="instructions">
                Use prime factors or Venn diagrams
                to find each answer. Crossing digits
                will help you.
              </p>

              <div className="clues">
                {["Across", "Down"].map(
                  (direction) => (
                    <div
                      className="clue-group"
                      key={direction}
                    >
                      <h3>{direction}</h3>

                      {clues
                        .filter(
                          (clue) =>
                            clue.direction ===
                            direction
                        )
                        .map((clue) => (
                          <button
                            key={clue.number}
                            disabled={finished}
                            className={`clue ${
                              activeClue ===
                              clue.number
                                ? "selected"
                                : ""
                            } ${
                              results[
                                clue.number
                              ] || ""
                            }`}
                            onClick={() =>
                              selectClue(
                                clue.number
                              )
                            }
                          >
                            <strong>
                              {clue.number}
                            </strong>

                            <span>
                              {clue.clue}

                              {results[
                                clue.number
                              ] ===
                                "correct" && (
                                <small className="status-text">
                                  Correct
                                </small>
                              )}
                            </span>
                          </button>
                        ))}
                    </div>
                  )
                )}
              </div>

              <div className="selected-clue">
                <strong>
                  {selected.number}{" "}
                  {selected.direction}:
                </strong>{" "}
                {selected.clue}
              </div>

              {finished && (
                <div className="completion-card">
                  <h3>
                    Crossword completed
                  </h3>

                  <div className="time-results">
                    <div className="time-result">
                      <span>Your time</span>
                      <strong>
                        {formatTime(
                          elapsedTime
                        )}
                      </strong>
                    </div>

                    <div className="time-result">
                      <span>Best time</span>
                      <strong>
                        {bestTime === null
                          ? "—"
                          : formatTime(
                              bestTime
                            )}
                      </strong>
                    </div>
                  </div>
                </div>
              )}

              <div className="actions">
                {!finished ? (
                  <>
                    <button
                      className="action"
                      onClick={checkClue}
                    >
                      Check clue
                    </button>

                    <button
                      className="action blue"
                      onClick={checkAll}
                    >
                      Check all
                    </button>

                    <button
                      className="action yellow"
                      onClick={useHint}
                    >
                      Use hint ({hints})
                    </button>

                    <button
                      className="action grey"
                      onClick={() =>
                        reset(true)
                      }
                    >
                      Reset
                    </button>
                  </>
                ) : (
                  <button
                    className="action"
                    onClick={() =>
                      window.location.reload()
                    }
                  >
                    Try another crossword
                  </button>
                )}
              </div>
            </section>
          </div>
        )}
      </section>
    </main>
  );
}
